import { FC, memo, useCallback, useMemo } from 'react'
import { ValueChangedEvent as TextBoxValueChangedEvent } from 'devextreme/ui/text_box'
import { ValueChangedEvent as SelectBoxValueChangedEvent } from 'devextreme/ui/select_box'
import { ValueChangedEvent as TagBoxValueChangedEvent } from 'devextreme/ui/tag_box'
import _ from 'lodash'
import {
  ParamValue,
  SearchFilter,
  dateFilterValueToString,
  stringToDateFilterValue,
} from 'utils/filter'
import { CrewTextBox } from 'components/devextreme/crewTextBox'
import { assertNever } from '@crew/utils'
import { DisplayAnonymousFile, FileSearchOptions } from 'enums/app'
import { CrewTagBox } from 'components/devextreme/crewTagbox/crewTagBox'
import { useTagDataSource } from 'hooks/dataSource/useTagDataSource'
import { EntityType } from '@crew/enums/domain'
import { useEntityRecordDataSource } from 'hooks/dataSource/useEntityRecordDataSource'
import { useProjectGroupDataSource } from 'hooks/dataSource/useProjectGroupDataSource'
import { CrewSelectBox } from 'components/devextreme/crewSelectBox'
import {
  CrewDateFilterBox,
  CrewDateFilterBoxValueChangedEvent,
} from 'components/devextreme/crewDateFilterBox'
import { useUserDataSource } from 'hooks/dataSource/useUserDataSource'
import { useTranslation } from '@crew/modules/i18n'

type FileSearchInputProps = {
  filter: SearchFilter
  updateSearchValue: (filter: SearchFilter, value: ParamValue) => void
}

export const CrewFileSearchInput: FC<FileSearchInputProps> = memo(
  ({ filter, updateSearchValue }) => {
    //set data source for tag dropdown
    const { selectTagDataSource } = useTagDataSource(EntityType.File)
    const tagDataSource = selectTagDataSource

    // custom data source for related item
    const projectDataSource = useEntityRecordDataSource(EntityType.Project)

    // custom data source for project group
    const projectGroupDataSource = useProjectGroupDataSource()

    // custom data source for created by user
    const createdByUserDataSource = useUserDataSource(true)

    // custom data source for updated by user
    const updatedByUserDataSource = useUserDataSource(true)

    const { t } = useTranslation()

    // 匿名ファイル表示のDataSource
    const anonymousFileDataSource = useMemo(() => {
      return Object.values(DisplayAnonymousFile).map((display) => {
        return {
          id: display.value,
          name: t(display.text),
        }
      })
    }, [t])

    // キーワードの変更
    const handleKeywordChanged = useCallback(
      (e: TextBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        if (e.value === '') {
          updateSearchValue(filter, undefined)
          return
        }
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // change tag
    const handleTagChanged = useCallback(
      (e: TagBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // change project group
    const handleProjectGroupChanged = useCallback(
      (e: TagBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // プロジェクトの変更
    const handleProjectChanged = useCallback(
      (e: SelectBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // 作成者の変更
    const handleCreatedByChanged = useCallback(
      (e: SelectBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // 更新者の変更
    const handleUpdatedByChanged = useCallback(
      (e: SelectBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    // 作成日の変更
    const handleCreatedAtChanged = useCallback(
      (e: CrewDateFilterBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        if (e.value) {
          updateSearchValue(filter, dateFilterValueToString(e.value))
        }
      },
      [filter, updateSearchValue]
    )

    // 更新日の変更
    const handleUpdatedAtChanged = useCallback(
      (e: CrewDateFilterBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        if (e.value) {
          updateSearchValue(filter, dateFilterValueToString(e.value))
        }
      },
      [filter, updateSearchValue]
    )

    // 匿名ファイル表示の変更
    const handleAnonymousFileChanged = useCallback(
      (e: SelectBoxValueChangedEvent) => {
        if (_.isEqual(e.value, e.previousValue)) return
        updateSearchValue(filter, e.value)
      },
      [filter, updateSearchValue]
    )

    switch (filter.field) {
      case FileSearchOptions.Keyword.id:
        return (
          <CrewTextBox
            id={FileSearchOptions.Keyword.id}
            name={FileSearchOptions.Keyword.name}
            value={filter.value as string}
            showClearButton={true}
            mode="search"
            onValueChanged={handleKeywordChanged}
          />
        )

      case FileSearchOptions.AnonymousFile.id:
        return (
          <CrewSelectBox
            id={FileSearchOptions.AnonymousFile.id}
            name={FileSearchOptions.AnonymousFile.name}
            displayExpr="name"
            valueExpr="id"
            dataSource={anonymousFileDataSource}
            minSearchLength={0}
            showClearButton={true}
            value={filter.value}
            onValueChanged={handleAnonymousFileChanged}
            searchEnabled={false}
          />
        )

      case FileSearchOptions.Tag.id:
        return (
          <CrewTagBox
            id={FileSearchOptions.Tag.id}
            name={FileSearchOptions.Tag.name}
            displayExpr="name"
            valueExpr="id"
            dataSource={tagDataSource}
            searchEnabled={true}
            popupSearchEnabled={true}
            searchMode="contains"
            searchExpr="name"
            minSearchLength={0}
            maxDisplayedTags={2}
            value={filter.value as string[]}
            onValueChanged={handleTagChanged}
          />
        )

      case FileSearchOptions.ProjectId.id:
        return (
          <CrewSelectBox
            id={FileSearchOptions.ProjectId.id}
            name={FileSearchOptions.ProjectId.name}
            displayExpr="name"
            valueExpr="id"
            dataSource={projectDataSource}
            minSearchLength={0}
            showClearButton={true}
            value={filter.value}
            onValueChanged={handleProjectChanged}
            searchEnabled={true}
            searchExpr="name"
          />
        )
      case FileSearchOptions.ProjectGroupId.id:
        return (
          <CrewTagBox
            id={FileSearchOptions.ProjectGroupId.id}
            name={FileSearchOptions.ProjectGroupId.name}
            displayExpr="name"
            valueExpr="id"
            dataSource={projectGroupDataSource}
            searchEnabled={true}
            popupSearchEnabled={true}
            searchMode="contains"
            searchExpr="name"
            minSearchLength={0}
            maxDisplayedTags={2}
            value={filter.value as string[]}
            onValueChanged={handleProjectGroupChanged}
          />
        )

      case FileSearchOptions.CreatedById.id:
        return (
          <CrewSelectBox
            id={FileSearchOptions.CreatedById.id}
            name={FileSearchOptions.CreatedById.name}
            displayExpr="displayName"
            valueExpr="id"
            dataSource={createdByUserDataSource}
            minSearchLength={0}
            showClearButton={true}
            value={filter.value}
            onValueChanged={handleCreatedByChanged}
            searchEnabled={true}
            searchExpr="displayName"
          />
        )
      case FileSearchOptions.UpdatedById.id:
        return (
          <CrewSelectBox
            id={FileSearchOptions.UpdatedById.id}
            name={FileSearchOptions.UpdatedById.name}
            displayExpr="displayName"
            valueExpr="id"
            dataSource={updatedByUserDataSource}
            minSearchLength={0}
            showClearButton={true}
            value={filter.value}
            onValueChanged={handleUpdatedByChanged}
            searchEnabled={true}
            searchExpr="displayName"
          />
        )
      case FileSearchOptions.CreatedAt.id:
        return (
          <CrewDateFilterBox
            value={stringToDateFilterValue(filter.value as string)}
            onValueChange={handleCreatedAtChanged}
          />
        )
      case FileSearchOptions.UpdatedAt.id:
        return (
          <CrewDateFilterBox
            value={stringToDateFilterValue(filter.value as string)}
            onValueChange={handleUpdatedAtChanged}
          />
        )

      default:
        // assertNever has value type never, so we need to cast filter.field to never
        assertNever(filter.field as never, 'Invalid filter field')
    }
  }
)
